import { QUANT_QUALITY_PENALTY, findMachine, findModel } from './catalog.js';
import { estimateFit } from './fit.js';
import { benchLocalOllama } from './ollama-inference.js';
import type { BenchmarkProfile, BenchmarkResult, Quantization, RuntimeId, TaskCategory } from './types.js';

const BENCHMARK_CACHE: BenchmarkResult[] = [];
const CACHE_LISTENERS = new Set<() => void>();

function notify(): void {
  for (const l of CACHE_LISTENERS) l();
}

export function subscribeResults(listener: () => void): () => void {
  CACHE_LISTENERS.add(listener);
  return () => CACHE_LISTENERS.delete(listener);
}

export function getAllResults(): BenchmarkResult[] {
  return [...BENCHMARK_CACHE].sort((a, b) => b.timestamp - a.timestamp);
}

export function getResultsFor(modelId: string, machineId?: string): BenchmarkResult[] {
  return getAllResults().filter((r) => r.modelId === modelId && (!machineId || r.machineId === machineId));
}

export function leaderboardFor(
  machineId: string,
  taskCategory: TaskCategory = 'coding',
): Array<{
  modelId: string;
  quantization: Quantization;
  runtime: RuntimeId;
  avgTps: number;
  avgQuality: number;
  runs: number;
}> {
  const rows = BENCHMARK_CACHE.filter((r) => r.machineId === machineId && r.taskCategory === taskCategory);
  const grouped: Record<string, BenchmarkResult[]> = {};
  for (const r of rows) {
    const key = `${r.modelId}|${r.quantization}|${r.runtime}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  }
  return Object.entries(grouped)
    .map(([key, items]) => {
      const [modelId, quantization, runtime] = key.split('|') as [string, Quantization, RuntimeId];
      const avgTps = items.reduce((s, r) => s + r.tokensPerSecond, 0) / items.length;
      const avgQuality = items.reduce((s, r) => s + r.qualityScore, 0) / items.length;
      return { modelId, quantization, runtime, avgTps, avgQuality, runs: items.length };
    })
    .sort((a, b) => b.avgQuality - a.avgQuality);
}

function taskCategoryQualityMultiplier(task: TaskCategory): number {
  const map: Record<TaskCategory, number> = {
    coding: 1.0,
    review: 0.95,
    debugging: 0.9,
    reasoning: 0.93,
    docs: 1.02,
    'tool-use': 0.85,
  };
  return map[task] ?? 1.0;
}

function jitter(value: number, range: number, seed: number): number {
  const pseudo = Math.sin(seed * 13.37) * 10000;
  const noise = (pseudo - Math.floor(pseudo) - 0.5) * 2 * range;
  return Math.max(0, value + value * noise);
}

export interface SimulateOpts {
  machineId: string;
  modelId: string;
  quantization: Quantization;
  runtime: RuntimeId;
  task: TaskCategory;
  seed?: number;
}

export function simulateRun(opts: SimulateOpts): BenchmarkResult {
  const machine = findMachine(opts.machineId);
  const model = findModel(opts.modelId);
  if (!machine || !model) {
    throw new Error(`Unknown machine or model: ${opts.machineId}/${opts.modelId}`);
  }
  const fit = estimateFit(machine, model, opts.quantization, opts.runtime);
  const seed = opts.seed ?? Math.floor(Date.now() % 100000);
  const taskMult = taskCategoryQualityMultiplier(opts.task);

  const tps = jitter(fit.expectedTps, 0.08, seed);
  const ttft = jitter(fit.expectedTtftMs, 0.1, seed + 1);
  const outputTokens = 400 + Math.floor((seed % 17) * 11);
  const latency = Math.round(ttft + (outputTokens / Math.max(1, tps)) * 1000);

  const qualityBase = Math.max(0, model.baseQualityScore - QUANT_QUALITY_PENALTY[opts.quantization]);
  const qualityScore = Math.round(jitter(qualityBase * taskMult, 0.04, seed + 2) * 10) / 10;
  const passRate = Math.round(Math.min(1, qualityScore / 100 + 0.25) * 100) / 100;
  const stabilityScore = Math.round((0.78 + Math.min(0.2, fit.headroomRatio / 20)) * 100) / 100;
  const toolUseSuccess =
    opts.task === 'tool-use'
      ? Math.round(Math.min(0.99, qualityScore / 110) * 100) / 100
      : Math.round(Math.min(0.99, qualityScore / 95) * 100) / 100;

  const memoryUseGb = Math.round(fit.requiredGb * 10) / 10;
  const vramUseGb = machine.accel === 'apple-silicon' ? memoryUseGb : Math.min(machine.vramGb, memoryUseGb);

  const diffQuality = Math.round(Math.min(100, qualityScore * 0.98 + (seed % 3)) * 10) / 10;
  const repairSuccess = Math.round(Math.min(0.98, qualityScore / 100) * 100) / 100;
  const agentCompletionScore =
    Math.round(Math.min(100, qualityScore * 0.6 + Math.min(fit.expectedTps, 30) + stabilityScore * 20) * 10) /
    10;

  const headroomBoost = Math.min(0.2, fit.headroomRatio / 15);
  const sourceBoost = 0;
  const benchConfidence =
    Math.round(Math.min(1, 0.55 + headroomBoost + stabilityScore * 0.2 + sourceBoost) * 100) / 100;

  const id = `bench_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`;

  const result: BenchmarkResult = {
    id,
    machineId: opts.machineId,
    modelId: opts.modelId,
    quantization: opts.quantization,
    runtime: opts.runtime,
    taskCategory: opts.task,
    tokensPerSecond: Math.round(tps * 10) / 10,
    latencyMs: latency,
    timeToFirstTokenMs: Math.round(ttft),
    totalTokens: outputTokens,
    memoryUseGb,
    vramUseGb,
    qualityScore,
    passRate,
    stabilityScore,
    toolUseSuccess,
    diffQuality,
    repairSuccess,
    agentCompletionScore,
    benchConfidence,
    totalCost: 0,
    timestamp: Date.now(),
    source: 'estimate',
  };
  return result;
}

export function recordResult(result: BenchmarkResult): void {
  BENCHMARK_CACHE.push(result);
  notify();
}

export interface RunProfileOpts {
  machineId: string;
  modelId: string;
  quantization: Quantization;
  runtime: RuntimeId;
  profile: BenchmarkProfile;
  onProgress?: (done: number, total: number, latest: BenchmarkResult) => void;
  stepDelayMs?: number;
  useLiveOllama?: boolean;
  customPrompts?: Partial<Record<TaskCategory, string>>;
}

export async function runBenchmarkProfile(opts: RunProfileOpts): Promise<BenchmarkResult[]> {
  const steps: Array<{ task: TaskCategory; seed: number }> = [];
  let seedCounter = Math.floor(Date.now() % 100000);
  for (const task of opts.profile.tasks) {
    for (let i = 0; i < opts.profile.iterations; i++) {
      steps.push({ task, seed: seedCounter++ });
    }
  }
  const results: BenchmarkResult[] = [];
  let done = 0;
  const model = findModel(opts.modelId);
  const liveTag = opts.useLiveOllama && opts.runtime === 'ollama' ? model?.ollamaTag : undefined;
  for (const step of steps) {
    await new Promise((r) => setTimeout(r, opts.stepDelayMs ?? 140));
    let result: BenchmarkResult | null = null;
    if (liveTag) {
      result = await benchLocalOllama({
        machineId: opts.machineId,
        modelId: opts.modelId,
        runtime: opts.runtime,
        ollamaTag: liveTag,
        quantization: opts.quantization,
        task: step.task,
      });
    }
    if (!result) {
      result = simulateRun({
        machineId: opts.machineId,
        modelId: opts.modelId,
        quantization: opts.quantization,
        runtime: opts.runtime,
        task: step.task,
        seed: step.seed,
      });
    }
    recordResult(result);
    results.push(result);
    done += 1;
    opts.onProgress?.(done, steps.length, result);
  }
  return results;
}

export interface RunMultiRuntimeOpts {
  machineId: string;
  modelId: string;
  quantization: Quantization;
  runtimes: RuntimeId[];
  profile: BenchmarkProfile;
  onProgress?: (done: number, total: number, latest: BenchmarkResult) => void;
  stepDelayMs?: number;
  useLiveOllama?: boolean;
}

export async function runMultiRuntimeBenchmark(opts: RunMultiRuntimeOpts): Promise<BenchmarkResult[]> {
  const totalSteps = opts.runtimes.length * opts.profile.tasks.length * opts.profile.iterations;
  let aggregateDone = 0;
  const all: BenchmarkResult[] = [];
  for (const runtime of opts.runtimes) {
    const results = await runBenchmarkProfile({
      machineId: opts.machineId,
      modelId: opts.modelId,
      quantization: opts.quantization,
      runtime,
      profile: opts.profile,
      stepDelayMs: opts.stepDelayMs,
      useLiveOllama: opts.useLiveOllama,
      onProgress: (_done, _total, latest) => {
        aggregateDone += 1;
        opts.onProgress?.(aggregateDone, totalSteps, latest);
      },
    });
    all.push(...results);
  }
  return all;
}

export function seedCommunityData(): void {
  if (BENCHMARK_CACHE.some((r) => r.source === 'community')) return;
  const pairs: Array<{ machine: string; model: string; quant: Quantization; runtime: RuntimeId }> = [
    { machine: 'rtx-4090-desktop-64', model: 'qwen-2.5-coder-32b', quant: 'q5_k_m', runtime: 'llama-cpp' },
    { machine: 'rtx-4090-desktop-64', model: 'qwen-2.5-coder-14b', quant: 'q8_0', runtime: 'llama-cpp' },
    { machine: 'rtx-4090-desktop-64', model: 'codestral-22b', quant: 'q5_k_m', runtime: 'ollama' },
    { machine: 'rtx-4090-desktop-64', model: 'llama-3.3-70b', quant: 'q4_k_m', runtime: 'llama-cpp' },
    { machine: 'macbook-pro-m3-max', model: 'qwen-2.5-coder-14b', quant: 'q5_k_m', runtime: 'mlx' },
    { machine: 'macbook-pro-m3-max', model: 'qwen-2.5-coder-7b', quant: 'q8_0', runtime: 'mlx' },
    { machine: 'macbook-pro-m3-max', model: 'deepseek-coder-v2-16b', quant: 'q5_k_m', runtime: 'ollama' },
    { machine: 'macbook-pro-m4-max-64', model: 'qwen-2.5-coder-32b', quant: 'q6_k', runtime: 'mlx' },
    { machine: 'macbook-pro-m4-max-64', model: 'llama-3.3-70b', quant: 'q4_k_m', runtime: 'mlx' },
    { machine: 'macbook-air-m2-16', model: 'qwen-2.5-coder-7b', quant: 'q4_k_m', runtime: 'ollama' },
    { machine: 'macbook-air-m2-16', model: 'qwen-2.5-coder-3b', quant: 'q5_k_m', runtime: 'ollama' },
    { machine: 'macbook-air-m2-16', model: 'phi-4-14b', quant: 'q4_k_m', runtime: 'ollama' },
    { machine: 'mac-mini-m4-24', model: 'qwen-2.5-coder-14b', quant: 'q4_k_m', runtime: 'ollama' },
    { machine: 'mac-mini-m4-24', model: 'deepseek-coder-v2-16b', quant: 'q4_k_m', runtime: 'ollama' },
    { machine: 'rtx-3060-12-pc', model: 'qwen-2.5-coder-7b', quant: 'q5_k_m', runtime: 'ollama' },
    { machine: 'rtx-3060-12-pc', model: 'qwen-2.5-coder-14b', quant: 'q4_k_m', runtime: 'ollama' },
    { machine: 'rtx-3060-12-pc', model: 'codestral-22b', quant: 'q3_k_m', runtime: 'lm-studio' },
  ];
  const tasks: TaskCategory[] = ['coding', 'review', 'debugging'];
  let seed = 42;
  for (const p of pairs) {
    for (const task of tasks) {
      for (let iter = 0; iter < 2; iter++) {
        const r = simulateRun({
          machineId: p.machine,
          modelId: p.model,
          quantization: p.quant,
          runtime: p.runtime,
          task,
          seed: seed++,
        });
        r.source = 'community';
        r.timestamp = Date.now() - (seed % 1000) * 3600_000;
        BENCHMARK_CACHE.push(r);
      }
    }
  }
  notify();
}
